import type { VForm } from "vuetify/components";
import type { Component } from "vue";
import type { ModalWidth } from "@/constants/modals";
import type { ErrorPayload } from "@/_store/errors.module.ts";
import { TicketType, TicketTrigger } from "@/constants/tickets.ts";
import { Service } from "@/constants/cloud-apps.ts";

export type GenericCallback<Params extends any[] = any[], ReturnType = any> = (
  ...params: Params
) => ReturnType;

export interface DataValue<T = any> {
  loading: boolean;
  error?: boolean;
  data?: T;
}

export interface Paged<T> {
  total: number;
  items: T[];
}
export interface Pagination {
  page: number;
  pageSize: number;
}

export interface OpenSearchQuery {
  [key: string]: any;
}

export interface OpenSearchCursor {
  nextPage?: OpenSearchQuery;
  prevPage?: OpenSearchQuery;
}

export type VuetifyFormRef = InstanceType<typeof VForm> | null;

export interface RequestError {
  description: string;
  errors?: string[] | ErrorPayload[];
  errorCode?: string;
}

export interface ConfirmationDialogConfig<T = any> {
  item?: T;
  action: string;
  callback: GenericCallback;
  disable?: boolean;
  disclaimer?: string;
  text?: string;
  width?: ModalWidth;
  title?: string;
}

export interface ComponentDialogConfig<T> {
  item?: T;
  action: string;
  component: Component;
  width: number;
  disable?: boolean;
  disclaimer?: string;
  callback: GenericCallback;
  closeCallback?: GenericCallback;
  hideFooter?: boolean;
  scrollable?: boolean;
  customErrorHandling?: boolean;
  showValidationAlert?: boolean;
  title?: string;
}

export interface ListDialogConfig<T> {
  item?: T;
  action: string;
  width?: ModalWidth;
}

export interface TimeFrame {
  start: string;
  end: string;
}

export interface Selection<T> {
  isAllPages: boolean;
  include: Array<T>;
  exclude: Array<T>;
}
export interface TicketTypeToFullDetailsComponentMap {
  [TicketType.MALWARE_IN_CLOUD_DRIVE]: Component;
  [TicketType.SUSPECTED_DATA_EXPOSURE_BY_EMAIL]: Component;
  [TicketType.SUSPECTED_DATA_EXPOSURE_BY_SHARING]: Component;
  [TicketType.SUSPECTED_COMPLIANCE_VIOLATION_BY_EMAIL]: Component;
  [TicketType.SUSPECTED_COMPLIANCE_VIOLATION_BY_SHARING]: Component;
  [TicketType.USER_INACTIVITY]: Component;
  [TicketType.SUSPECTED_COMPLIANCE_VIOLATION_BY_DEVICE]: Component;
  [TicketType.MALWARE_IN_EMAIL_ATTACHMENTS]: Component;
  [TicketType.EMAIL_PHISHING]: Component;
  [TicketType.ABNORMAL_ADMIN_ACTIVITY]: Component;
  [TicketType.SUSPECTED_BOT_ATTACKS]: Component;
  [TicketType.ACCESS_PERMISSIONS_VIOLATION]: Component;
  [TicketType.SUSPECTED_IDENTITY_COMPROMISE]: Component;
  [TicketType.EDR_DETECTION]: Component;
  [TicketType.PROXY_MISCONFIGURATION]: Component;
  [TicketType.IMPOSSIBLE_TRAVELER]: Component;
  [TicketType.PROMPT_INJECTION]: Component;
  [TicketType.SUSPECTED_UNIFIED_SENSITIVE_DATA_VIOLATION_BY_EMAIL]: Component;
  [TicketType.SUSPECTED_UNIFIED_SENSITIVE_DATA_VIOLATION_BY_SHARING]: Component;
  [TicketType.SUSPECTED_UNIFIED_SENSITIVE_DATA_VIOLATION_BY_DEVICE]: Component;
}

export interface TicketTriggerToFullDetailsComponentMap {
  [TicketTrigger.DEVICE_PASSWORD_MISSING]: Component;
  [TicketTrigger.MASS_DELETE]: Component;
  [TicketTrigger.MASS_DOWNLOAD]: Component;
}

export interface TicketUserToAddToProtection {
  email: string;
  service: Service;
}

export enum AppContext {
  MSP = "mspApp",
  SINGLE_WORKSPACE = "singleWorkspaceApp",
}

export type DeepPartial<T> = {
  [K in keyof T]?: T[K] extends object ? DeepPartial<T[K]> : T[K];
};
